import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import type { ReportWeek } from '../types/report.types';
import {
  CARD_BORDER,
  DISABLED_FG,
  MUTED_FG,
  PAGE_BG,
  WEEK_DOT_BG,
  WEEK_DOT_BORDER,
  WEEK_RAIL_BG,
} from '../reportTokens';

/**
 * Width given to each week on the strip: the dot plus its date label. A short
 * history spreads out to fill the row; past what fits, the strip scrolls
 * sideways instead of crushing the labels together.
 */
const WEEK_SLOT = 64;
/** Height of the dot's hit area, which the rail runs through the middle of. */
const DOT_AREA = 30;

// Square, and as tall as the dot row: the strip aligns to its top edge, so the
// arrows sit level with the dots only while the two heights match.
const chevronSx = (disabled: boolean) => ({
  width: DOT_AREA,
  height: DOT_AREA,
  flex: '0 0 auto',
  borderRadius: '8px',
  border: '1px solid',
  borderColor: CARD_BORDER,
  bgcolor: WEEK_DOT_BG,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  color: disabled ? DISABLED_FG : MUTED_FG,
  cursor: disabled ? 'default' : 'pointer',
  p: 0,
});

interface WeekNavigatorProps {
  weeks?: ReportWeek[];
  /** The agency's brand colour — the same one the KPI cards and toggle use for "you are here". */
  accent: string;
}

/**
 * The ring on the weeks not being read: the agency's colour, lightened, so
 * the whole strip is one hue. A fixed purple sat beside an orange or green
 * agency's current week as a second brand. The fixed token stays as the
 * fallback for a colour MUI cannot parse.
 */
const dotRing = (accent: string) => {
  // `alpha` does not throw on a malformed hex like `#12345g`: it returns
  // `rgba(NaN, …)`, which the browser drops, leaving a text-coloured ring.
  if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(accent)) return WEEK_DOT_BORDER;
  try {
    return alpha(accent, 0.5);
  } catch {
    return WEEK_DOT_BORDER;
  }
};

/**
 * The design's week strip. `report.weeks` arrives newest-first — it is built
 * for "which token is this one" lookups, not for drawing a timeline — so it
 * is reversed once here rather than asking every caller to remember the
 * backend's order. The result reads oldest (left) to current (right).
 *
 * Every week the client was sent, each dated under its dot. A dot with only a
 * hover tooltip told a client nothing on a phone, and a long history is
 * scrolled to the week being read rather than opening on the oldest one.
 *
 * Absent, or empty, on a v2 snapshot and on the authenticated agency route,
 * neither of which builds it: the page renders without this strip rather
 * than throwing on a missing list.
 */
function WeekNavigator({ weeks, accent }: WeekNavigatorProps) {
  const navigate = useNavigate();
  const scrollerRef = useRef<HTMLDivElement>(null);

  const ordered = weeks ? [...weeks].reverse() : [];
  const currentIndex = ordered.findIndex(week => week.current);
  const currentToken = ordered[currentIndex]?.token;
  const otherRing = dotRing(accent);

  // Centre the current week once the strip is laid out, and again whenever
  // the reader steps to another week or the list itself changes. With no week
  // marked (a test send is not one of them) it opens on the newest weeks
  // rather than on the oldest.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const dot = scroller.querySelector<HTMLElement>('[aria-current="true"]');
    scroller.scrollLeft = dot
      ? dot.offsetLeft - scroller.clientWidth / 2 + dot.offsetWidth / 2
      : scroller.scrollWidth;
  }, [currentToken, weeks]);

  if (ordered.length === 0) return null;

  const atStart = currentIndex <= 0;
  const atEnd = currentIndex === -1 || currentIndex >= ordered.length - 1;

  const goTo = (index: number) => {
    const target = ordered[index];
    if (target) navigate(`/report/${target.token}`);
  };

  return (
    <Box
      className='report-no-print'
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
    >
      <Box
        component='button'
        type='button'
        aria-label='Previous week'
        disabled={atStart}
        onClick={() => goTo(currentIndex - 1)}
        sx={chevronSx(atStart)}
      >
        ‹
      </Box>
      <Box
        ref={scrollerRef}
        sx={{
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          scrollbarWidth: 'thin',
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            minWidth: ordered.length * WEEK_SLOT,
          }}
        >
          {/* Joins the first dot to the last; one week has nothing to join. */}
          {ordered.length > 1 && (
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: WEEK_SLOT / 2,
                right: WEEK_SLOT / 2,
                top: DOT_AREA / 2,
                height: '3px',
                mt: '-1.5px',
                borderRadius: '9999px',
                bgcolor: WEEK_RAIL_BG,
              }}
            />
          )}
          {ordered.map((week, index) => {
            const isCurrent = index === currentIndex;
            // "Dec 28, 2025" is wider than a slot on one line, so a week
            // from another year puts its year on a second line.
            const date = week.date ?? week.title;
            const { year } = week;
            return (
              <Box
                key={week.token}
                component='button'
                type='button'
                title={week.title}
                aria-label={week.title}
                aria-current={isCurrent ? 'true' : undefined}
                onClick={() => goTo(index)}
                sx={{
                  position: 'relative',
                  width: WEEK_SLOT,
                  flex: '0 0 auto',
                  border: 0,
                  background: 'none',
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                <Box
                  component='span'
                  sx={{
                    height: DOT_AREA,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component='span'
                    data-testid='week-dot'
                    sx={{
                      display: 'block',
                      width: isCurrent ? 18 : 11,
                      height: isCurrent ? 18 : 11,
                      borderRadius: '50%',
                      bgcolor: isCurrent ? accent : WEEK_DOT_BG,
                      border: '2px solid',
                      borderColor: isCurrent ? accent : otherRing,
                      boxShadow: `0 0 0 3px ${PAGE_BG}`,
                    }}
                  />
                </Box>
                <Box
                  component='span'
                  aria-hidden
                  sx={{
                    fontSize: '11px',
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    color: isCurrent ? accent : MUTED_FG,
                    fontWeight: isCurrent ? 600 : 400,
                    textAlign: 'center',
                  }}
                >
                  {date}
                  {year && (
                    <Box component='span' sx={{ display: 'block' }}>
                      {year}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box
        component='button'
        type='button'
        aria-label='Next week'
        disabled={atEnd}
        onClick={() => goTo(currentIndex + 1)}
        sx={chevronSx(atEnd)}
      >
        ›
      </Box>
    </Box>
  );
}

export default WeekNavigator;
